/**
 * Public pages (Login / Signup) read a `?tenant=<id>` query param, hit the
 * no-auth /api/public/tenant-theme endpoint, and apply the firm's theme
 * (row-hover, accent, brand color) via the same CSS variables ThemeApplier
 * uses post-login. The returned tenant name + logo are handed back so the
 * page can optionally render a "Sign in to <Firm Name>" header.
 *
 * Mounted inside Login/Signup. The CSS variables are reset on unmount so
 * a user bouncing to a plain /login after visiting a themed URL doesn't
 * see stale colors.
 */
import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { api } from "@/lib/api";

const DEFAULT_ROW_HOVER = "#EFF6FF";
const DEFAULT_ACCENT = "#DBEAFE";

export function usePublicTenantTheme() {
  const { search } = useLocation();
  const [branding, setBranding] = useState(null);

  useEffect(() => {
    const params = new URLSearchParams(search);
    const tenant = params.get("tenant");
    const slug = params.get("f") || params.get("slug");
    if (!tenant && !slug) return;
    let cancelled = false;
    (async () => {
      try {
        const qp = tenant
          ? `tenant=${encodeURIComponent(tenant)}`
          : `slug=${encodeURIComponent(slug)}`;
        const { data } = await api.get(`/public/tenant-theme?${qp}`);
        if (cancelled || !data?.tenant_id) return;
        setBranding(data);
        const root = document.documentElement;
        if (data.row_hover) root.style.setProperty("--ll-row-hover", data.row_hover);
        if (data.accent) {
          root.style.setProperty("--ll-accent-soft", data.accent);
          root.style.setProperty("--ll-mint", data.accent);
        }
      } catch { /* silent — unknown tenant leaves defaults in place */ }
    })();
    return () => {
      cancelled = true;
      // Reset to defaults so the next page load isn't stuck on this firm.
      const root = document.documentElement;
      root.style.setProperty("--ll-row-hover", DEFAULT_ROW_HOVER);
      root.style.setProperty("--ll-accent-soft", DEFAULT_ACCENT);
      root.style.removeProperty("--ll-mint");
    };
  }, [search]);

  return branding; // { tenant_id, name, logo_base64, brand_color, row_hover, accent, preset } | null
}
